import * as React from 'react';
import { BarElement, CategoryScale, Chart as ChartJS, LinearScale, Tooltip as ChartTooltip } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { Box, Paper, Typography } from '@mui/material';
import { ExperimentReport } from './types';

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTooltip);

// An A/B test's versions side by side on what it is judged on: one small bar
// chart per metric, one bar per version, each version in the same color
// throughout.

type Arm = NonNullable<ExperimentReport['arms']>[number];

const COLORS = ['#4F46E5', '#10B981', '#F59E0B', '#DC2626', '#0EA5E9', '#8B5CF6'];

const money = (dollars: number) => `${dollars < 0 ? '−' : ''}$${Math.abs(dollars).toFixed(2)}`;
const percent = (share: number) => `${(share * 100).toFixed(1)}%`;

type Metric = {
  title: string;
  hint: string;
  value: (arm: Arm) => number | null;
  format: (value: number) => string;
  // The tooltip's second line, for the numbers behind the value.
  detail: (arm: Arm) => string;
};

// Profit per view: the model's middle estimate once it reads the test, the raw
// figure before.
const profitPerView = (arm: Arm) =>
  arm.profitPerViewCents
    ? arm.profitPerViewCents.mid / 100
    : arm.profitPerAcceptCents == null || !arm.viewed
      ? null
      : (arm.profitPerAcceptCents * arm.accepted) / arm.viewed / 100;

const METRICS: Metric[] = [
  {
    title: 'Take rate',
    hint: 'accepted ÷ viewed',
    value: arm => (arm.viewed ? arm.accepted / arm.viewed : null),
    format: percent,
    detail: arm => `${arm.accepted} of ${arm.viewed} views`,
  },
  {
    title: 'Revenue / view',
    hint: 'the extra charged, per offer seen',
    value: arm => (arm.viewed ? arm.revenue / arm.viewed : null),
    format: money,
    detail: arm => `${money(arm.revenue)} over ${arm.viewed} views`,
  },
  {
    title: 'Profit / view',
    hint: 'est. margin per offer seen: what the test is judged on',
    value: profitPerView,
    format: money,
    detail: arm =>
      arm.profitPerViewCents
        ? `90% range ${money(arm.profitPerViewCents.low / 100)} – ${money(arm.profitPerViewCents.high / 100)}`
        : arm.profitPerAcceptCents == null
          ? 'margin unknown'
          : `${money(arm.profitPerAcceptCents / 100)} per yes`,
  },
  {
    title: 'Chance best',
    hint: 'likely the most profitable per view; call it at 90%',
    value: arm => arm.chanceBest,
    format: percent,
    detail: arm => (arm.chanceBest == null ? 'shown once every version has enough views' : `${arm.viewed} views`),
  },
];

function MetricChart({ metric, arms }: { metric: Metric; arms: Arm[] }) {
  const values = arms.map(metric.value);
  const data = {
    labels: arms.map(arm => arm.label),
    datasets: [
      {
        data: values,
        backgroundColor: arms.map((_, index) => COLORS[index % COLORS.length]),
        borderRadius: 3,
        maxBarThickness: 28,
      },
    ],
  };
  const options = {
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      // A Dashboard-page chart registers chartjs-plugin-datalabels globally.
      datalabels: { display: false },
      legend: { display: false },
      tooltip: {
        displayColors: false,
        callbacks: {
          title: (items: any[]) => items[0].label,
          label: (item: any) => `${metric.title}: ${item.raw == null ? '—' : metric.format(item.raw)}`,
          afterLabel: (item: any) => metric.detail(arms[item.dataIndex]),
        },
      },
    },
    scales: {
      x: { beginAtZero: true, grid: { color: '#F1F5F9' }, ticks: { maxTicksLimit: 4, callback: (value: number | string) => metric.format(Number(value)) } },
      y: { grid: { display: false }, ticks: { display: false } },
    },
  };
  const known = values.filter((value): value is number => value != null);
  const best = known.length ? Math.max(...known) : null;
  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Typography variant="body2" fontWeight={600}>{metric.title}</Typography>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>{metric.hint}</Typography>
      {known.length ? (
        <Box sx={{ height: 36 + arms.length * 30 }}>
          <Bar data={data as any} options={options as any} />
        </Box>
      ) : (
        <Typography variant="caption" color="text.secondary">No data yet.</Typography>
      )}
      {best != null && known.length > 1 && (
        <Typography variant="caption" color="text.secondary">
          Leads: {arms[values.indexOf(best)].label} ({metric.format(best)})
        </Typography>
      )}
    </Paper>
  );
}

export function VersionsChart({ arms }: { arms: Arm[] }) {
  if (!arms.some(arm => arm.viewed > 0)) return null;
  return (
    <Box sx={{ my: 1.5 }} aria-label="versions compared">
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1 }}>
        {arms.map((arm, index) => (
          <Box key={arm.designId} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: COLORS[index % COLORS.length] }} />
            <Typography variant="caption">{arm.label}</Typography>
          </Box>
        ))}
      </Box>
      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' } }}>
        {METRICS.map(metric => (
          <MetricChart key={metric.title} metric={metric} arms={arms} />
        ))}
      </Box>
    </Box>
  );
}
